'use client';

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'next/navigation';
import Image from 'next/image';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import CertificateTemplate from '@/components/certificate/template-certificate';
import NotFound404Page from '@/components/NotFound404Page';
import { Button } from '@/components/ui/button';
import { getCertificateResultByID } from '@/lib/api/certificate-api';
import type { CertificateData } from '@/lib/api/certificate-api';
import { formatDate, parseDate } from '@/helpers/date-format';
import '@/styles/certificate.css';

export function CertificatePageContent() {
  const params = useParams();
  const certificateID = params.certificateID as string;
  const certificateRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<CertificateData | null>(null);
  const [image, setImage] = useState<string | null>(null);
  // No ID in the URL is a synchronous "not found" — never enter the loading state for it
  const [loading, setLoading] = useState(Boolean(certificateID));
  const [loadError, setLoadError] = useState<'not-found' | 'error' | null>(null);
  // Rasterization is only needed for the PDF download — if it fails we still
  // show the certificate by rendering the template directly.
  const [imageFailed, setImageFailed] = useState(false);

  // Memoized formatted values for dynamic certificate content
  const certificateProps = useMemo(() => {
    if (!data) return null;
    const user = data.userUid;
    const userName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Certificate recipient' : 'Certificate recipient';
    const rawDate = (data as any).earnedAt ?? data.createdAt ?? (data as any).updatedAt;
    const issueDate = formatDate(rawDate) || '—';
    return {
      userName,
      learningPathTitle: data.title ?? 'Learning Path',
      issueDate,
      certificateId: data.uid,
    };
  }, [data]);

  // Fetch certificate data by its backend uid (e.g. ACAD-39116531FB).
  // Every exit path must clear `loading`, otherwise the page spins forever.
  useEffect(() => {
    if (!certificateID) return;
    let cancelled = false;
    getCertificateResultByID(certificateID)
      .then((result) => {
        if (cancelled) return;
        if (result.status === 'ok') {
          setData(result.data);
        } else {
          setLoadError(result.status);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [certificateID]);

  // Generate high-quality certificate image
  useEffect(() => {
    if (!data || !certificateRef.current || image) return;

    let cancelled = false;

    const generateImage = async () => {
      try {
        // Wait for fonts and resources to load
        await document.fonts.ready;
        await Promise.all([
          document.fonts.load('400 24px Tercia'),
          document.fonts.load('400 32px Tercia'),
          document.fonts.load('400 12px Inter'),
          document.fonts.load('400 16px Inter'),
          document.fonts.load('600 18px Inter'),
        ]);
        
        // Additional delay for logo SVG
        await new Promise((r) => setTimeout(r, 500));
      } catch (error) {
        console.warn('Font loading error:', error);
      }

      // Generate image with retries
      let dataUrl = '';
      const maxAttempts = 5;

      for (let attempt = 0; attempt < maxAttempts && !cancelled; attempt++) {
        if (attempt > 0) await new Promise((r) => setTimeout(r, 100));

        try {
          // Detect current theme for background color
          const isDark = document.documentElement.classList.contains('dark');
          const bgColor = isDark ? '#1A1919' : '#FFFFFF';
          
          dataUrl = await toPng(certificateRef.current!, {
            quality: 1.0,
            pixelRatio: 3,
            backgroundColor: bgColor,
            cacheBust: true,
            skipFonts: false,
            style: {
              '-webkit-font-smoothing': 'antialiased',
              '-moz-osx-font-smoothing': 'grayscale',
              'text-rendering': 'optimizeLegibility',
            } as any,
            fetchRequestInit: {
              mode: 'cors',
              credentials: 'omit',
            },
          });

          // Check if image is valid
          if (dataUrl.length > 20000) break;
        } catch (error) {
          console.error('Certificate generation error:', error);
        }
      }

      if (cancelled) return;

      if (dataUrl) {
        setImage(dataUrl);
      } else {
        // Every attempt failed — fall back to the live DOM instead of spinning
        setImageFailed(true);
      }
    };

    generateImage();

    return () => {
      cancelled = true;
    };
  }, [data, image]);

  // Download as PDF - High quality export
  const downloadPDF = useCallback(async () => {
    if (!image) return;

    // Use 3x resolution for PDF (2376x1836)
    const width = 792 * 3;
    const height = 612 * 3;

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [width, height],
      compress: false,  // Disable compression for maximum quality
      hotfixes: ['px_scaling'],
    });

    // Add image with maximum quality settings
    pdf.addImage(
      image,
      'PNG',
      0,
      0,
      width,
      height,
      undefined,
      'SLOW'  // Use SLOW for best quality (no compression)
    );

    pdf.save('contentstack-academy-certificate.pdf');
  }, [image]);

  // Share on LinkedIn
  const shareLinkedIn = useCallback(() => {
    if (!data) return;

    const rawDate = (data as any).earnedAt ?? data.createdAt ?? (data as any).updatedAt;
    const date = parseDate(rawDate) ?? new Date();
    const params = new URLSearchParams({
      startTask: 'CERTIFICATION_NAME',
      name: data.title,
      organizationId: '18395537',
      issueYear: date.getFullYear().toString(),
      issueMonth: (date.getMonth() + 1).toString(),
      certUrl: window.location.href,
      certId: data.uid,
    });

    window.open(`https://www.linkedin.com/profile/add?${params}`, '_blank');
  }, [data]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-1">
        <div className="certificate-spinner" />
      </div>
    );
  }

  // Transient failure (network, 5xx) — don't claim the certificate doesn't exist
  if (loadError === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface-1 px-4 text-center">
        <h1 className="cs-h2 text-text-default-strong">We couldn&apos;t load this certificate</h1>
        <p className="text-text-default">
          Something went wrong on our end. Please check your connection and try again.
        </p>
        <Button variant="PrimaryFillButton" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    );
  }

  // Definitive 404 from the backend, or no usable data
  if (loadError === 'not-found' || !data || !certificateProps) {
    return <NotFound404Page />;
  }

  return (
    <div className="bg-surface-1 py-12">
      <div className="container mx-auto px-4">
        {/* Certificate Display */}
        <div className="mb-8 flex justify-center">
          {image ? (
            <Image
              src={image}
              alt="Certificate"
              width={792}
              height={612}
              className="rounded-lg shadow-2xl"
              priority
              unoptimized
            />
          ) : imageFailed ? (
            // .certificate-container is a fixed 792px wide and its responsive rule
            // only scales via transform (which keeps the layout box), so contain it
            <div className="max-w-full overflow-x-auto rounded-lg shadow-2xl">
              <CertificateTemplate {...certificateProps} />
            </div>
          ) : (
            <div className="certificate-loading">
              <div className="certificate-spinner" />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex justify-center gap-4">
            <Button
              variant="PrimaryFillButton"
              onClick={downloadPDF}
              disabled={!image}
            >
              Download Certificate
            </Button>
            <Button
              variant="SecondaryFillButton"
              onClick={shareLinkedIn}
            >
              Share on LinkedIn
            </Button>
          </div>
          {imageFailed && (
            <p className="text-sm text-text-default">
              PDF download is unavailable right now, but your certificate is shown above.
            </p>
          )}
        </div>

        {/* Hidden Certificate Render */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={certificateRef}>
            <CertificateTemplate {...certificateProps} />
          </div>
        </div>
      </div>
    </div>
  );
}
